import { Tooltip } from "antd";
import { useMemo } from "react";
import { datetime } from "../../../../../../util/library/datetime";
import { useSelector } from "../../../../../../util/store/store";
import { aDayMillisecond, OPERATION_TYPE, VALIDATION_STATUS } from "../../../../../../util/varibles/constants";
import { durationTxt } from "../../../../../../util/varibles/global";
import { selectPlan } from "../../../../constants";
import { getTypesAndUnitOfTreatment } from "../../../../util";
import { getEditOp, IVesselOfMain, useEditOp } from "../../EditOperationContext";
import styles from "./style.module.scss";
import { IMainOp } from "./SupportOpContext";

const WIDTH_SIDEBAR = 150;
const HEIGHT_VESSEL = 28;
const HEIGHT_OPERATION = 20;

const operationContent: { [id: string]: (data: IMainOp['data']) => any } = {
    [OPERATION_TYPE.HARVEST]: (data) => {
        const { start, finish, operation } = data;
        const result = [
            `${datetime(start).format('HH:mm')} - ${datetime(finish).format('HH:mm')}`,
            `${durationTxt(finish - start)}`,
            `${operation.site_name} - ${operation.sub_operations.map(item => item.unit_id)}`
        ]
        return result.join(' | ')
    },
    [OPERATION_TYPE.TREATMENT]: (data) => {
        const { start, finish, operation } = data;
        const { types } = getTypesAndUnitOfTreatment(operation.tasks || []);
        const result = [
            `${datetime(start).format('HH:mm')} - ${datetime(finish).format('HH:mm')}`,
            `${durationTxt(finish - start)}`,
            `${operation.site_name} - ${operation.sub_operations.map(item => item.unit_id)} (${Object.keys(types).map(key => types[key]).join(', ')})`
        ]
        return result.join(' | ')
    },
    [OPERATION_TYPE.SUPPORT]: (data) => {
        const { start, finish, operation } = data;
        const result = [
            `${datetime(start).format('HH:mm')} - ${datetime(finish).format('HH:mm')}`,
            `${durationTxt(finish - start)}`,
            `#${operation.operation_code}`
        ]
        return result.join(' | ')
    }
}

const VesselOps: React.FC<{ vesselIndex: number, data: IVesselOfMain, mainOp: IMainOp['data'], }> = ({ vesselIndex, data, mainOp }) => {
    const level = useSelector(selectPlan.level);
    const opsRoot = useSelector(selectPlan.ops);
    const { id: mainId } = mainOp.operation;
    const { id: vesselId } = data;
    const [supportOps] = useEditOp(state => state.supportOperations[mainId]?.[vesselId]?.ops)

    const ops = useMemo(() => {
        const keyOps = level[vesselId] || {};
        return Object.keys(keyOps).reduce((rs: IMainOp['data'][], key) => {
            const currentOp = opsRoot[key];
            if (currentOp && key !== mainId && !currentOp.operation.parallel) {
                return [...rs, {
                    start: currentOp.activity_log[0].est_start_time,
                    finish: currentOp.activity_log[currentOp.activity_log.length - 1].est_start_time,
                    operation: currentOp.operation,
                    type: OPERATION_TYPE.UNAVAILABLE
                }]
            }
            return rs;
        }, [])
    }, [opsRoot, level, mainId, vesselId])

    return <>
        {ops.map(item => <Operation key={item.operation.id} data={item} vesselIndex={vesselIndex} />)}
        {(supportOps || []).map(item => !item.is_delete && <Operation
            key={item.operation.id}
            data={{
                operation: item.operation,
                start: item.activity_log[0].est_start_time,
                finish: item.activity_log[item.activity_log.length - 1].est_start_time,
                type: item.operation.operation_type,
                status: item.status,                // For plan page
                message: item.message
            }}
            vesselIndex={vesselIndex}
        />)}
    </>
}

const Operation: React.FC<{ data: IMainOp['data'], vesselIndex?: number }> = ({ data, vesselIndex = 0 }) => {
    const { start, finish, type, status, message = "" } = data;

    const style = useMemo(() => {
        return {
            height: HEIGHT_OPERATION,
            top: (vesselIndex * HEIGHT_VESSEL) + (HEIGHT_VESSEL - HEIGHT_OPERATION) / 2,
            '--op-left': start,
            '--op-width': finish - start
        }
    }, [start, finish, vesselIndex])

    const content = operationContent[type]?.(data) || "";

    const FinalView = <div className={styles.operation} data-op-type={type} data-error={status === VALIDATION_STATUS.ERROR} data-is-main={data.is_main} style={style} title={content}>
        <span className="truncate">{content}</span>
    </div>

    if (message.length > 0) {
        return <Tooltip title={message}>
            {FinalView}
        </Tooltip>
    }

    return FinalView
}

interface IProps {
    mainOp: IMainOp['data']
}

const Calendar: React.FC<IProps> = ({ mainOp }) => {
    const [supportOperations] = useEditOp(state => state.supportOperations);
    const getOp = getEditOp();
    const { vessel } = getOp();
    const { start, finish } = mainOp;
    const { id: mainId } = mainOp.operation

    const selectedVessels = useMemo(() => Object.keys(supportOperations[mainId] || {}).filter(key => supportOperations[mainId][key].checked), [supportOperations, mainId]);

    const { days, duration } = useMemo(() => {
        let begin = datetime(start).subtract(2, 'day').startOf('day');
        const end = datetime(finish).add(2, 'day').endOf('day').time;
        const days = [];
        while (begin.diff(end) <= 0) {
            days.push(begin);
            begin = begin.add(1, 'day');
        }

        return { days, duration: days.length * aDayMillisecond }
    }, [start, finish]);

    return <div className={styles.calendar} style={{ '--min-sidebar': `${WIDTH_SIDEBAR}px`, '--height-vessel': `${HEIGHT_VESSEL}px` } as any}>
        <div className="flex flex-col relative">
            <div className={styles.days}>
                <div className='text-right' data-lev='sidebar'>
                    {days[0]?.format("MMMM YYYY")}
                </div>
                {days.map((day, index) => <div key={index} data-lev='day'>
                    <div className="text-center text-comment">{day.format('ddd DD')}</div>
                </div>)}
            </div>
            <div
                className={styles.calenderVessels}
                style={{
                    '--begin': Math.max(days[0]?.time, 1),
                    '--duration': Math.max(duration, 1)
                } as any}
            >
                <div>
                    <div className={styles.calenderVessel} data-lev='sidebar'>
                        {vessel?.name}
                    </div>
                    {selectedVessels.map((id) => {
                        const { name } = supportOperations[mainId][id];
                        return <div key={id} className={styles.calenderVessel} data-lev='sidebar'>
                            {name}
                        </div>;
                    })}
                </div>
                <div className="relative overflow-hidden">
                    {selectedVessels.map((id, i) => <VesselOps key={id} vesselIndex={i + 1} data={supportOperations[mainId][id]} mainOp={mainOp} />)}
                    <Operation data={{ ...mainOp, is_main: true }} />
                </div>
            </div>
        </div>
    </div>;
}
export default Calendar;